
import * as messages from '@radnuc/messages';
import * as thrift from 'thrift';
import { Buffer } from 'buffer';

import { uuidToString, generateRandomUuid } from './UUID';
import { Dataset, invToEncounter } from './Data';

type Investigation = messages.Encounter.Encounter | messages.Rad.Investigation;

async function timeQuery(start_time: number, end_time: number, filters: string[], lane?:number) {
    if(lane === undefined)
        lane = 0;
    let port = window.location.port;
    if (window.location.port === '3000')
        port = '8301';
    const response = await fetch('http://' + window.location.hostname + ':'+port+'/TGW1hNlSwjaNFF96k4QQ9RXFNeiEqRab/' +
        `query/time/${start_time}/${end_time}?filter=${filters.join(',')}&lane=${lane}`);
    if (response.status !== 200)
        throw new Error(`Bad return code: ${response.status}`);

    const data = await response.arrayBuffer();

    const typed_data = Buffer.from(data);
    const transport = new thrift.TFramedTransport(typed_data);
    const protocol = new thrift.TBinaryProtocol(transport);

    const result = new messages.Query.QueryResult();
    result.read(protocol);
    return result;
}

async function uuidQuery(uuid: messages.Core.UUID, filters: string[], timeout: number = 5000, lane?:number) {
    if(lane === undefined)
        lane = 0;
    const controller = new AbortController();
    const signal = controller.signal;

    const timeoutId = setTimeout(() => controller.abort(), timeout);
    let port = window.location.port;
    if (window.location.port === '3000')
        port = '8301';
    const response = await fetch('http://' + window.location.hostname + ':'+port+'/TGW1hNlSwjaNFF96k4QQ9RXFNeiEqRab/' +
        `query/uuid/${uuidToString(uuid)}?filter=${filters.join(',')}&lane=${lane}`, {signal});
    if (response.status !== 200)
        throw new Error(`Bad return code: ${response.status}`);
    clearTimeout(timeoutId);

    const data = await response.arrayBuffer();

    const typed_data = Buffer.from(data);
    const transport = new thrift.TFramedTransport(typed_data);
    const protocol = new thrift.TBinaryProtocol(transport);

    const result = new messages.Query.UUIDQueryResult();
    result.read(protocol);
    return result;
}

export async function queryAllInvestigations() {
    const result = await timeQuery(0, 2000000000000, [messages.Rad.InvestigationTopic]);
    return result.investigations as messages.Rad.Investigation[];
}

export async function queryAllInvestigationsAsEncounter() {
    const result = await timeQuery(0, 2000000000000, [messages.Rad.InvestigationTopic,messages.Encounter.EncounterTopic]);

    let encounters =  result.encounters as messages.Encounter.Encounter[];
    if(encounters === null) {
        //No encounters in the query so declare a new one
        encounters = new Array<messages.Encounter.Encounter>();
    }
    //FIXME Converting investigations to encounters
    console.log('CONVERTING Investigations to Encounters');
    const investigations =  result.investigations as messages.Rad.Investigation[];
    investigations.forEach((inv)=>{
        //Is Investigation type. convert to encounters
        const thisInv = invToEncounter(inv);
        encounters.push(thisInv);
    })
    return encounters;

}

export async function queryAllCoralResults() {
    const result = await timeQuery(0, 2000000000000, [messages.Contextual.CoralResultTopic]);
    return result.coralResults as messages.Contextual.CoralResult[];
}

export async function queryInvestigationData(inv: Investigation, padding: number, getImages?: boolean, lanes?: number[], getChemBioMet?:boolean) {

    let filters = [
        messages.Rad.RadiationReadingRebinnedTopic,
        messages.Core.GPSReadingTopic,
        messages.Rad.PCSResultTopic,
        messages.Rad.NeutronReadingTopic,
        messages.Rad.NeutronResultTopic,
        ];
    if(getChemBioMet !== undefined){
        filters.push(messages.Chem.ChemIdentificationResultTopic,
                messages.Chem.MetReading1HzTopic,
                messages.SigmaPlusBio.LIFReadingTopic);
    }
    if(getImages !== undefined){
        filters.push(messages.Contextual.ContextImageTopic,
                messages.Contextual.ContextImageAnalysisTopic,
                messages.Contextual.ContextTrack2DAnalysisTopic,
                messages.Contextual.ContextTrack3DAnalysisTopic);
    }
    let start_time = inv.start_time - padding;
    let end_time = inv.end_time + padding;
    const result = await timeQuery(start_time, end_time, filters);

    const attribution_filters = [
        messages.Contextual.SourceAttributionAnalysisTopic,
        messages.Contextual.SourceAttributionSummaryTopic];
    start_time = inv.start_time;
    end_time = inv.end_time + 20000;
    const attributionResult = await timeQuery(start_time, end_time, attribution_filters);

    let attribution: Map<String, messages.Contextual.SourceAttributionSummary> | undefined = new Map<String,messages.Contextual.SourceAttributionSummary>();
    if(getImages){
        attributionResult.sourceAttributionSummaries.forEach((element: messages.Contextual.SourceAttributionSummary) => {
            if (uuidToString(element.investigation_id) === uuidToString(inv.id) &&
                (element.source_image !== null && element.source_rect !== null))
                attribution?.set(uuidToString(element.id),element)

        });
    } else {
        attributionResult.sourceAttributionSummaries.forEach((element: messages.Contextual.SourceAttributionSummary) => {
            if (uuidToString(element.investigation_id) === uuidToString(inv.id)){
                //Check to see if this attribution id exists
                if(attribution instanceof Map){
                    if(!attribution?.has(uuidToString(element.id))){
                        //Attribution doesn't have this attribution id
                        attribution.set(uuidToString(element.id),element)

                        /*
                        //DEBUG START *******************
                        let extra_attribution = new messages.Contextual.SourceAttributionSummary();
                        extra_attribution.source_image = element.source_image;
                        extra_attribution.investigation_id = element.investigation_id;
                        extra_attribution.id = generateRandomUuid();
                        extra_attribution.source_rect = new messages.Contextual.ImageRect()
                        extra_attribution.source_rect.down = 500;
                        extra_attribution.source_rect.up = 600;
                        extra_attribution.source_rect.left = 500;
                        extra_attribution.source_rect.right = 600;
                        //HACK add another attribution
                        attribution.set(uuidToString(extra_attribution.id),extra_attribution)
                        //DEBUG END *******************
                        */

                    } else {
                        const this_uuid_attribution = attribution.get(uuidToString(element.id))
                        if(this_uuid_attribution !== undefined && this_uuid_attribution?.time < element.time){
                            //replace with newer message
                            attribution.set(uuidToString(element.id),element)
                        }
                    }
                }
            }
        });
    }
    if(attribution.size === 0)
        attribution = undefined;


    const attributionAnalysis = attributionResult.sourceAttributions?.reduce((prev, cur) =>
        prev?.time > cur.time? prev: cur, undefined);

    const images_all_lanes = new Map<number,messages.Contextual.ContextImageReference[]>()
    images_all_lanes.set(0,result.images);
    const imageAnalyses_all_lanes = new Map<number,messages.Contextual.ContextImageAnalysis[]>()
    imageAnalyses_all_lanes.set(0,result.imageAnalyses);
    const track2d_all_lanes = new Map<number,messages.Contextual.ContextTrack2DAnalysis[]>()
    track2d_all_lanes.set(0,result.track2d);
    const track3d_all_lanes = new Map<number,messages.Contextual.ContextTrack3DAnalysis[]>()
    track3d_all_lanes.set(0,result.track3d);

    if(lanes !== undefined){
        if(lanes.includes(1)){
            //Images from other lanes
            const lane = 1
            const filters2 = [
                messages.Contextual.ContextImageTopic,
                messages.Contextual.ContextImageAnalysisTopic,
                messages.Contextual.ContextTrack2DAnalysisTopic,
                messages.Contextual.ContextTrack3DAnalysisTopic];
            const start_time = inv.start_time - padding;
            const end_time = inv.end_time + padding;
            const result2 = await timeQuery(start_time, end_time, filters2, lane);

            //Add to all lanes
            images_all_lanes.set(1,result2.images);
            imageAnalyses_all_lanes.set(1,result2.imageAnalyses);
            track2d_all_lanes.set(1,result2.track2d);
            track3d_all_lanes.set(1,result2.track3d);
        }
    }


    const dataset = new Dataset(images_all_lanes, imageAnalyses_all_lanes, track2d_all_lanes, track3d_all_lanes, result.gps,
        result.rebinned_radiation, result.pcs, result.neutronReadings, result.neutronResults, attributionAnalysis,
        attribution,result.metReadings,result.chemIdentificationResults,result.lifReadings);
    console.log(dataset)
    return dataset;
}

/*
export async function queryInvestigationData(inv: messages.Rad.Investigation, padding: number) {
    const filters = [
        messages.Rad.RadiationReadingRebinnedTopic,
        messages.Core.GPSReadingTopic,
        messages.Rad.PCSResultTopic,
        messages.Rad.NeutronReadingTopic,
        messages.Rad.NeutronResultTopic,
        //messages.Contextual.ContextImageTopic
        //messages.Contextual.ContextImageAnalysisTopic,
        //messages.Contextual.ContextTrack2DAnalysisTopic,
        //messages.Contextual.ContextTrack3DAnalysisTopic
    ];
    let start_time = inv.start_time - padding;
    let end_time = inv.end_time + padding;
    const result = await timeQuery(start_time, end_time, filters);

    const attribution_filters = [
        messages.Contextual.SourceAttributionAnalysisTopic,
        messages.Contextual.SourceAttributionSummaryTopic];
    start_time = inv.start_time;
    end_time = inv.end_time + 20000;
    const attributionResult = await timeQuery(start_time, end_time, attribution_filters);

    let attribution = undefined;
    attributionResult.sourceAttributionSummaries.forEach((element: messages.Contextual.SourceAttributionSummary) => {
        if (uuidToString(element.investigation_id) === uuidToString(inv.id))// &&
                //element.source_image !== null )//&& element.source_rect !== null)
            attribution = element;
    });

    const attributionAnalysis = attributionResult.sourceAttributions?.reduce((prev, cur) =>
        prev?.time > cur.time? prev: cur, undefined);

    const combined_images = new Map<number,messages.Contextual.ContextImageReference[]>()
    combined_images.set(0,result.images);

    const combined_imageAnalyses = new Map<number,messages.Contextual.ContextImageAnalysis[]>()
    combined_imageAnalyses.set(0,result.imageAnalyses);

    const combined_track2d = new Map<number,messages.Contextual.ContextTrack2DAnalysis[]>()
    combined_track2d.set(0,result.track2d);

    const combined_track3d = new Map<number,messages.Contextual.ContextTrack3DAnalysis[]>()
    combined_track3d.set(0,result.track3d);

    const dataset = new Dataset(combined_images, combined_imageAnalyses, combined_track2d, combined_track3d, result.gps,
        result.rebinned_radiation, result.pcs, result.neutronReadings, result.neutronResults, attributionAnalysis,
        attribution);
    console.log(dataset)
    return dataset;
}
*/

export async function queryImageData(img: messages.Contextual.ContextImageReference, timeout: number = 5000, lane?: number,) {
    if(lane === undefined)
        lane = 0;
    const result = await uuidQuery(img.id, [messages.Contextual.ContextImageTopic], timeout, lane);
    if (!result.items || !result.items[0].image)
        throw new Error('Failed to find image');

    return result.items[0].image as messages.Contextual.ContextImage;
}

type ParseResult<T> =
    | { parsed: T; hasError: false; error?: undefined }
    | { parsed?: undefined; hasError: true; error?: unknown }


const safeJsonParse = <T>(guard: (o: any) => o is T) => (text: string): ParseResult<T> => {
    const parsed = JSON.parse(text)
    return guard(parsed) ? { parsed, hasError: false } : { hasError: true }
}

type N42Result = { payload?: string, errors?: string[] }
function isN42Result(o: any): o is N42Result {
    return "payload" in o || "errors" in o
  }


export async function queryN42(id: messages.Core.UUID, timeout: number = 10000): Promise<string> {
    const controller = new AbortController();
    const signal = controller.signal;

    const timeoutId = setTimeout(() => controller.abort(), timeout);
    let port = window.location.port;
    if (window.location.port === '3000')
        port = '8301';
    const response = await fetch('http://' + window.location.hostname + ':'+port+'/TGW1hNlSwjaNFF96k4QQ9RXFNeiEqRab/n42/' +
        uuidToString(id), {signal});
    if (response.status !== 200)
        throw new Error(`Bad return code: ${response.status}`);
    clearTimeout(timeoutId);

    const data = safeJsonParse(isN42Result)(await response.text());
    if (data.hasError)
        throw new Error('Failed to parse response');

    if (data.parsed.errors || !data.parsed.payload) {
        throw new Error('Query errors')
    }

    return data.parsed.payload;
}

export async function queryPdf(id: messages.Core.UUID, timeout: number = 30000): Promise<Blob> {
    const controller = new AbortController();
    const signal = controller.signal;

    const timeoutId = setTimeout(() => controller.abort(), timeout);
    let port = window.location.port;
    if (window.location.port === '3000')
        port = '8301';
    const response = await fetch('http://' + window.location.hostname + ':'+port+'/TGW1hNlSwjaNFF96k4QQ9RXFNeiEqRab/pdf/' +
        uuidToString(id), {signal});
    if (response.status !== 200)
        throw new Error(`Bad return code: ${response.status}`);
    clearTimeout(timeoutId);

    if(response === null)
        throw new Error('Failed to parse response');
    const data = await response!.blob();

    return data

    /*
    const data = safeJsonParse(isN42Result)(await response.text());
    if (data.hasError)
        throw new Error('Failed to parse response');

    if (data.parsed.errors || !data.parsed.payload) {
        throw new Error('Query errors')
    }

    return data.parsed.payload;
    */
}
